import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

const COUNTRY_CODES = [
  { group: 'South Asia',  label: 'India (+91)',         value: '+91'  },
  { group: 'South Asia',  label: 'Sri Lanka (+94)',      value: '+94'  },
  { group: 'South Asia',  label: 'Bangladesh (+880)',    value: '+880' },
  { group: 'South Asia',  label: 'Nepal (+977)',         value: '+977' },
  { group: 'SE Asia',     label: 'Singapore (+65)',      value: '+65'  },
  { group: 'SE Asia',     label: 'Malaysia (+60)',       value: '+60'  },
  { group: 'SE Asia',     label: 'Thailand (+66)',       value: '+66'  },
  { group: 'SE Asia',     label: 'Indonesia (+62)',      value: '+62'  },
  { group: 'SE Asia',     label: 'Philippines (+63)',    value: '+63'  },
  { group: 'SE Asia',     label: 'Vietnam (+84)',        value: '+84'  },
  { group: 'Middle East', label: 'UAE (+971)',           value: '+971' },
  { group: 'Middle East', label: 'Saudi Arabia (+966)',  value: '+966' },
  { group: 'Middle East', label: 'Qatar (+974)',         value: '+974' },
  { group: 'Middle East', label: 'Bahrain (+973)',       value: '+973' },
  { group: 'Middle East', label: 'Oman (+968)',          value: '+968' },
  { group: 'Middle East', label: 'Kuwait (+965)',        value: '+965' },
  { group: 'Diaspora',    label: 'USA / Canada (+1)',    value: '+1'   },
  { group: 'Diaspora',    label: 'UK (+44)',             value: '+44'  },
  { group: 'Diaspora',    label: 'Australia (+61)',      value: '+61'  },
  { group: 'Diaspora',    label: 'New Zealand (+64)',    value: '+64'  },
];
const GROUPS = [...new Set(COUNTRY_CODES.map(c => c.group))];

// ── Inline SVG icons ──────────────────────────────────────────────────────────
function IcPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9094AE"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function IcLock({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function IcCheck({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IcShield({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function IcChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9094AE"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

// ── CSS injected once ─────────────────────────────────────────────────────────
const PAGE_STYLES = (
  <style>{`
    .sb-input {
      font-family: 'Inter', -apple-system, sans-serif !important;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .sb-input:focus {
      outline: none;
      border-color: #2ABFAB !important;
      box-shadow: 0 0 0 3px rgba(42,191,171,0.12) !important;
    }
    .sb-input::placeholder { color: #B0B3C8; }
    .sb-select { appearance: none; -webkit-appearance: none; }
    .sb-textarea { resize: vertical; }

    .sb-submit {
      transition: transform 0.2s, box-shadow 0.2s;
      font-family: 'Inter', sans-serif;
    }
    .sb-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(42,191,171,0.45) !important;
    }
    .sb-submit:active:not(:disabled) { transform: translateY(0); }

    @keyframes sb-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .sb-page-spinner {
      width: 40px; height: 40px;
      border: 3px solid rgba(42,191,171,0.2);
      border-top-color: #2ABFAB;
      border-radius: 50%;
      animation: sb-spin 0.8s linear infinite;
    }
    .sb-btn-spinner {
      display: inline-block;
      width: 20px; height: 20px;
      border: 2.5px solid rgba(255,255,255,0.4);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: sb-spin 0.8s linear infinite;
    }

    .sb-content-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: 100%;
    }
    @media (min-width: 768px) {
      .sb-content-row {
        flex-direction: row;
        align-items: flex-start;
        gap: 24px;
      }
      .sb-left-col {
        flex: 0 0 480px;
        max-width: 480px;
      }
    }
  `}</style>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function CentreBookingPage() {
  const { slug } = useParams();
  const [centre,         setCentre]         = useState(null);
  const [healers,        setHealers]        = useState([]);
  const [status,         setStatus]         = useState('loading');
  const [form,           setForm]           = useState({
    name: '', mobile: '', email: '', condition: '', preferredHealer: '',
  });
  const [countryCode,    setCountryCode]    = useState('+91');
  const [submitting,     setSubmitting]     = useState(false);
  const [errorMsg,       setErrorMsg]       = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/book/${slug}`);
        if (active) {
          setCentre(r.data.data.centre);
          setHealers(r.data.data.healers || []);
          setStatus('ready');
        }
      } catch (err) {
        if (!active) return;
        setStatus('not_found');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.name.trim())   { setErrorMsg('Name is required.');   return; }
    if (!form.mobile.trim()) { setErrorMsg('Mobile is required.'); return; }
    if (!form.email.trim()) {
      setErrorMsg('Please enter your email address.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (!consentChecked) {
      setErrorMsg('Please agree to be contacted before submitting.');
      return;
    }
    const e164Mobile = `${countryCode}${form.mobile.replace(/\D/g, '')}`;
    setSubmitting(true);
    try {
      await api.post(`/book/${slug}`, {
        name:            form.name.trim(),
        mobile:          e164Mobile,
        email:           form.email.trim(),
        condition:       form.condition.trim(),
        preferredDate:   form.preferredDate   || undefined,
        preferredHealer: form.preferredHealer || undefined,
        consentGiven:    consentChecked,
      });
      setStatus('submitted');
    } catch (err) {
      const st = err.response?.status;
      if (st === 404) {
        setErrorMsg('This centre is not available.');
      } else if (st >= 500) {
        setErrorMsg('Something went wrong. Please try again.');
      } else {
        setErrorMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // Centre name initials for avatar fallback — computed once per centre load
  const centreInitials = useMemo(() => {
    if (!centre?.tenantName) return 'C';
    return centre.tenantName
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [centre]);

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div className="sb-page-spinner" />
        </div>
      </>
    );
  }

  // ── Not found ───────────────────────────────────────────────────────────────
  if (status === 'not_found') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div style={s.statusCard}>
            <div style={{ ...s.statusIcon, background: '#FFF2F2' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E05252"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 style={s.statusTitle}>Centre Not Found</h2>
            <p style={s.statusBody}>This centre is not available.</p>
          </div>
        </div>
      </>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <>
      {PAGE_STYLES}
      <div style={s.page}>

        {/* Dot grid texture overlay */}
        <div style={s.dotGrid} />

        {/* Header bar */}
        <header style={s.header}>
          <div style={s.headerInner}>
            <span style={s.logoText}>
              LifeChi<span style={{ color: '#2ABFAB' }}>AI</span>
            </span>
            <span style={s.poweredBy}>Powered by LifeChiAI</span>
          </div>
        </header>

        {/* Content column */}
        <div style={s.content}>

          <div style={s.logoBlock}>
            <img src="/logo-web.png" alt="LifeChiAI" style={s.logoImg} />
          </div>

          <div className="sb-content-row">

            {/* LEFT — Booking form */}
            <div className="sb-left-col" style={s.leftCol}>
              <div style={{ ...s.sectionCard, textAlign: 'left', overflow: 'hidden', padding: 0 }}>

                {status === 'submitted' ? (
                  /* Success state */
                  <div style={s.successWrap}>
                    <div style={s.successCircle}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                        stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <h2 style={s.successTitle}>Request Received!</h2>
                    <p style={s.successBody}>
                      Your request has been received. The centre will contact you to confirm your session.
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '28px 24px' }}>
                    <h2 style={s.formTitle}>Request a Session</h2>
                    <p style={s.formSubtitle}>Fill in your details and the centre will confirm your appointment</p>

                    <form onSubmit={handleSubmit} noValidate
                      style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 24 }}>

                      {errorMsg && (
                        <div style={s.errorBanner}>{errorMsg}</div>
                      )}

                      {/* Name */}
                      <div>
                        <label style={s.label}>
                          Your Name <span style={s.required}>*</span>
                        </label>
                        <input
                          name="name" value={form.name} onChange={handleChange}
                          className="sb-input" style={s.input}
                          placeholder="Full name" autoComplete="name"
                        />
                      </div>

                      {/* Country */}
                      <div>
                        <label style={s.label}>Country</label>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={countryCode}
                            onChange={e => setCountryCode(e.target.value)}
                            className="sb-input sb-select"
                            style={{ ...s.input, paddingRight: 36, cursor: 'pointer' }}
                          >
                            {GROUPS.map(group => (
                              <optgroup key={group} label={group}>
                                {COUNTRY_CODES.filter(c => c.group === group).map(c => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <span style={s.chevron}><IcChevron /></span>
                        </div>
                      </div>

                      {/* Mobile */}
                      <div>
                        <label style={s.label}>
                          Mobile Number <span style={s.required}>*</span>
                        </label>
                        <input
                          name="mobile" value={form.mobile} onChange={handleChange}
                          className="sb-input" style={s.input}
                          placeholder="10-digit number" type="tel" autoComplete="tel"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label style={s.label}>
                          Email address <span style={s.required}>*</span>
                        </label>
                        <input
                          name="email" value={form.email} onChange={handleChange}
                          className="sb-input" style={s.input}
                          type="email" placeholder="your@email.com" autoComplete="email"
                        />
                        <span style={s.hint}>You'll receive your login credentials at this address</span>
                      </div>

                      {/* Healing need */}
                      <div>
                        <label style={s.label}>What brings you here? (optional)</label>
                        <textarea
                          name="condition" value={form.condition} onChange={handleChange}
                          className="sb-input sb-textarea"
                          style={{ ...s.input, height: 'auto', minHeight: 90, padding: '12px 14px' }}
                          placeholder="Briefly describe your concern" rows={3}
                        />
                      </div>

                      {/* Preferred healer */}
                      {false && (
                        <div>
                          <label style={s.label}>Preferred healer (optional)</label>
                          <div style={{ position: 'relative' }}>
                            <select
                              name="preferredHealer"
                              value={form.preferredHealer}
                              onChange={handleChange}
                              className="sb-input sb-select"
                              style={{ ...s.input, paddingRight: 36, cursor: 'pointer' }}
                            >
                              <option value="">No preference</option>
                              {healers.map(h => (
                                <option key={h._id} value={h._id}>
                                  {h.firstName} {h.lastName}
                                  {h.specialisation ? ` — ${h.specialisation}` : ''}
                                </option>
                              ))}
                            </select>
                            <span style={s.chevron}><IcChevron /></span>
                          </div>
                        </div>
                      )}

                      {/* Consent */}
                      <div style={s.consentRow}>
                        <div
                          style={{ ...s.checkbox, ...(consentChecked ? s.checkboxOn : {}) }}
                          role="checkbox"
                          aria-checked={consentChecked}
                          tabIndex={0}
                          onClick={() => setConsentChecked(c => !c)}
                          onKeyDown={e => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              e.preventDefault();
                              setConsentChecked(c => !c);
                            }
                          }}
                        >
                          {consentChecked && (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                              stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </div>
                        <span style={s.consentText}>
                          I agree to be contacted by {centre?.tenantName} for healing sessions.{' '}
                          <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={s.privacyLink}>
                            Privacy Policy
                          </a>
                        </span>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        className="sb-submit"
                        style={{
                          ...s.submitBtn,
                          opacity: (!consentChecked || submitting) ? 0.75 : 1,
                          cursor:  (!consentChecked || submitting) ? 'not-allowed' : 'pointer',
                        }}
                        disabled={!consentChecked || submitting}
                      >
                        {submitting
                          ? <span className="sb-btn-spinner" />
                          : 'Request a Session'
                        }
                      </button>

                      <div style={s.privacyNote}>
                        <IcLock size={12} color="#9094AE" />
                        <span>Your information is protected under DPDPA 2023</span>
                      </div>

                    </form>
                  </div>
                )}
              </div>
            </div>{/* end sb-left-col */}

            {/* RIGHT — Info panel */}
            <div style={s.rightCol}>
              <div style={s.infoCard}>

                {/* Centre logo + name */}
                <div style={s.centreHeader}>
                  {centre?.logoUrl ? (
                    <img src={centre.logoUrl} alt={centre.tenantName} style={s.centreLogoImg} />
                  ) : (
                    <div style={s.centreAvatar}>{centreInitials}</div>
                  )}
                  <div>
                    <h1 style={s.centreName}>{centre?.tenantName}</h1>
                    {centre?.city && (
                      <div style={s.cityRow}>
                        <IcPin />
                        <span style={s.cityText}>{centre.city}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Verified badge */}
                <div style={s.verifiedBadge}>
                  <IcShield size={13} color="#2ABFAB" />
                  <span>Verified Pranic Healing Practitioner</span>
                </div>

                <div style={s.infoDivider} />

                <h2 style={s.infoHeading}>What is Pranic Healing?</h2>

                <p style={s.infoPara}>
                  Pranic Healing is a no-touch, no-drug energy healing system developed by Grand Master Choa Kok Sui. It works by cleansing depleted or congested energy from the body's energy field and replenishing it with fresh, vital prana — the life force that sustains the body and mind.
                </p>

                <p style={s.infoPara}>
                  Sessions are conducted either in-person or at a distance — both are equally effective. Most clients feel calmer and more grounded within hours. Each session typically lasts 30–60 minutes, with no physical contact required.
                </p>

                {/* Three key points */}
                <div style={s.keyPoints}>
                  {[
                    'No drugs. No touch. No side effects.',
                    'Suitable for physical, emotional, and psychological conditions.',
                    'Practised in 130+ countries worldwide.',
                  ].map((pt, i) => (
                    <div key={i} style={s.keyPoint}>
                      <IcCheck size={14} color="#2ABFAB" />
                      <span style={s.keyPointText}>{pt}</span>
                    </div>
                  ))}
                </div>

                <p style={s.infoDisclaimer}>
                  Pranic Healing is a complementary modality and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified healthcare professional for medical conditions.
                </p>

              </div>
            </div>{/* end sb-right-col */}

          </div>{/* end sb-content-row */}

          {/* Footer */}
          <footer style={s.footer}>
            <p style={s.footerLine}>Powered by LifeChiAI · Energqubix AI Private Limited</p>
            <p style={s.footerLine}>
              © 2026 ·{' '}
              <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={s.footerAnchor}>
                Privacy Policy
              </a>
              {' '}· DPDPA 2023 Compliant
            </p>
          </footer>

        </div>
      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const s = {
  // Page shell
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F0F4FF 0%, #F7F8FC 50%, #EEF9F7 100%)',
    fontFamily: FONT,
  },
  dotGrid: {
    position: 'fixed',
    inset: 0,
    backgroundImage: 'radial-gradient(circle, #C8CBE0 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    opacity: 0.35,
    pointerEvents: 'none',
    zIndex: 0,
  },
  fullPage: {
    position: 'relative',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F0F4FF 0%, #F7F8FC 50%, #EEF9F7 100%)',
    fontFamily: FONT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },

  // Header
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    height: 52,
    backgroundColor: 'rgba(247,248,252,0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid #E2E4EF',
    boxShadow: '0 1px 4px rgba(13,15,31,0.06)',
    display: 'flex',
    alignItems: 'center',
  },
  headerInner: {
    width: '100%',
    maxWidth: 480,
    margin: '0 auto',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoText: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: 16,
    color: '#0D0F1F',
  },
  poweredBy: {
    fontSize: 11,
    color: '#9094AE',
    fontFamily: FONT,
  },

  // Content column
  content: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 960,
    margin: '0 auto',
    padding: '20px 16px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Base card
  sectionCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
  },

  // Two-column layout
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },
  rightCol: {
    flex: 1,
    minWidth: 0,
  },

  // Info card (right column)
  infoCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Centre identity
  centreHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  centreAvatar: {
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(42,191,171,0.3)',
    fontFamily: FONT,
  },
  centreLogoImg: {
    height: 56,
    width: 'auto',
    maxWidth: 120,
    objectFit: 'contain',
    borderRadius: 8,
    flexShrink: 0,
  },
  centreName: {
    fontFamily: FONT,
    fontSize: 20,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: 0,
  },
  cityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  cityText: {
    fontSize: 13,
    color: '#555870',
    fontFamily: FONT,
  },

  // Verified badge
  verifiedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#E8FAF8',
    border: '1px solid rgba(42,191,171,0.3)',
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#1A9E8C',
    alignSelf: 'flex-start',
    fontFamily: FONT,
  },

  infoDivider: {
    height: 1,
    background: '#E2E4EF',
    margin: '0 -4px',
  },

  infoHeading: {
    fontFamily: FONT,
    fontSize: 18,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: 0,
  },
  infoPara: {
    fontSize: 14,
    lineHeight: 1.7,
    color: '#555870',
    margin: 0,
    fontFamily: FONT,
  },

  // Key points
  keyPoints: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  keyPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  keyPointText: {
    fontSize: 14,
    color: '#2A2D45',
    lineHeight: 1.5,
    fontFamily: FONT,
  },

  infoDisclaimer: {
    fontSize: 12,
    color: '#9094AE',
    lineHeight: 1.5,
    margin: 0,
    fontFamily: FONT,
  },

  // Form
  formTitle: {
    fontFamily: FONT,
    fontSize: 18,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: 0,
  },
  formSubtitle: {
    fontSize: 13,
    color: '#555870',
    margin: '4px 0 0',
    fontFamily: FONT,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#2A2D45',
    marginBottom: 6,
    fontFamily: FONT,
  },
  required: { color: '#E05252' },
  input: {
    display: 'block',
    width: '100%',
    height: 46,
    padding: '0 14px',
    border: '1.5px solid #E2E4EF',
    borderRadius: 10,
    fontSize: 14,
    color: '#0D0F1F',
    background: '#FAFBFF',
    outline: 'none',
    fontFamily: FONT,
    boxSizing: 'border-box',
  },
  chevron: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  hint: {
    display: 'block',
    fontSize: 11,
    color: '#9094AE',
    marginTop: 4,
    fontFamily: FONT,
  },

  // Consent
  consentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  checkbox: {
    width: 18,
    height: 18,
    flexShrink: 0,
    marginTop: 2,
    border: '1.5px solid #CDD0E3',
    borderRadius: 5,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    userSelect: 'none',
    background: '#ffffff',
  },
  checkboxOn: {
    background: '#2ABFAB',
    borderColor: '#2ABFAB',
  },
  consentText: {
    fontSize: 13,
    color: '#555870',
    lineHeight: 1.5,
    fontFamily: FONT,
  },
  privacyLink: {
    color: '#2ABFAB',
    textDecoration: 'underline',
  },

  // Submit button
  submitBtn: {
    width: '100%',
    height: 52,
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    color: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(42,191,171,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
  },

  // Privacy note below button
  privacyNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    fontSize: 12,
    color: '#9094AE',
    fontFamily: FONT,
    marginTop: -4,
  },

  // Error banner
  errorBanner: {
    backgroundColor: '#FFF2F2',
    border: '1px solid #FCA5A5',
    color: '#DC2626',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: FONT,
  },

  // Status card (not_found)
  statusCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '40px 32px',
    textAlign: 'center',
    maxWidth: 360,
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
  },
  statusIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  statusTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: '0 0 8px',
    fontFamily: FONT,
  },
  statusBody: {
    fontSize: 14,
    color: '#555870',
    lineHeight: 1.6,
    fontFamily: FONT,
    margin: 0,
  },

  // Success state
  successWrap: {
    background: 'linear-gradient(135deg, #E8FAF8, #F0ECFD)',
    border: '1px solid rgba(42,191,171,0.2)',
    borderRadius: 20,
    padding: '40px 24px',
    textAlign: 'center',
  },
  successCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    boxShadow: '0 8px 24px rgba(42,191,171,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  successTitle: {
    fontFamily: FONT,
    fontSize: 22,
    fontWeight: 700,
    color: '#0D0F1F',
    marginTop: 20,
    marginBottom: 0,
  },
  successBody: {
    fontSize: 15,
    color: '#2A2D45',
    lineHeight: 1.65,
    marginTop: 12,
    fontFamily: FONT,
  },

  // Logo block
  logoBlock: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: 4,
    marginBottom: 8,
  },
  logoImg: {
    height: 44,
    width: 'auto',
  },

  // Footer
  footer: {
    marginTop: 8,
    paddingBottom: 8,
    textAlign: 'center',
  },
  footerLine: {
    fontSize: 12,
    color: '#9094AE',
    margin: '4px 0',
    fontFamily: FONT,
  },
  footerAnchor: {
    color: '#9094AE',
    textDecoration: 'underline',
  },
};
